<template>
  <div class="pa-4">
    <div v-if="loading" class="text-center">
      <v-skeleton-loader type="actions, divider, list-item-avatar, list-item-avatar, divider, article, actions">
      </v-skeleton-loader>
    </div>
    <div v-if="!loading && academy">
      <div v-if="isSmallerDisplay()">
        <v-tooltip bottom v-if="!isSmallerDisplay()">
          <template v-slot:activator="{ on }">
            <span v-on="on" v-if="academy && academy.currency" class="text-overline">
              {{ $t("message.currency") }}:
              {{ academy.currency }} ({{ academy.currencySymbol }})
            </span>
          </template>
          <span>{{ $t("message.academy_currency_change") }}</span>
        </v-tooltip>
      </div>
      <v-card-actions v-allowed="'view_products'">
        <v-tooltip bottom v-if="!isSmallerDisplay()">
          <template v-slot:activator="{ on }">
            <span v-on="on" v-if="academy && academy.currency" class="text-overline">
              {{ $t("message.currency") }}:
              {{ academy.currency }} ({{ academy.currencySymbol }})
            </span>
          </template>
          <span>{{ $t("message.academy_currency_change") }}</span>
        </v-tooltip>
        <v-spacer></v-spacer>
        <v-btn outlined class="mr-1" v-if="!isSmallerDisplay()" small elevation="0"
          @click="navigateTo({ name: 'bulksell' })" color="primary">
          <v-icon>mdi-cart-outline</v-icon>
          {{ $t("message.sell_items") }}
        </v-btn>
        <v-menu offset-y small>
          <template v-slot:activator="{ on }">
            <v-btn outlined :block="isSmallerDisplay()" v-on="on" slot="activator" small elevation="0" color="primary">
              {{ $t("message.actions") }}
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-allowed="'add_products'"
              @click="navigateTo({ name: 'manageproduct', params: { productId: 'new' } })">
              <v-list-item-title>
                <span class="mb-3 mr-1">
                  <v-icon size="20">mdi-view-grid-plus-outline</v-icon>
                </span>
                {{ $t("message.add_product") }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item v-if="paymentsReady" v-allowed="'add_products'"
              @click="navigateTo({ name: 'managepromocodes' })">
              <v-list-item-title>
                <span class="mb-3 mr-1">
                  <ReceiptTaxIcon size="20" class="hero-icon"></ReceiptTaxIcon>
                </span>
                {{ $t("message.promo_codes") }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item v-allowed="'sell_items'" @click="navigateTo({ name: 'sellamount' })">
              <v-list-item-title>
                <span class="mb-3 mr-1">
                  <v-icon size="20">mdi-currency-usd</v-icon>
                </span>
                {{ $t("message.sell_amount") }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item v-if="hasProducts" v-allowed="'sell_items'" @click="navigateTo({ name: 'bulksell' })">
              <v-list-item-title>
                <span class="mb-3 mr-1">
                  <v-icon size="20">mdi-cart-outline</v-icon>
                </span>
                {{ $t("message.sell_multiple_items") }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item v-if="hasProducts" v-allowed="'sell_items'" @click="getProductsCSV()">
              <v-list-item-title>
                <span class="mb-3 mr-1">
                  <CloudDownloadIcon size="20" class="hero-icon"></CloudDownloadIcon>
                </span>
                {{ $t("message.download_inventory") }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item v-if="isConnected" v-allowed="'sell_items'" @click="navigateTo({ name: 'publicmemberships' })">
              <v-list-item-title>
                <BookmarkAltIcon size="20" class="hero-icon"></BookmarkAltIcon>
                {{ $t("message.memberships_pubic_access") }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-card-actions>
      <div class="pa-1">
        <v-autocomplete v-if="trendline.products.length > 0" @change="getProductTrendline()" v-model="selectedProduct"
          :items="trendline.products" :label="$t('message.select_view_trendline')" item-text="text" item-value="value"
          clearable outlined dense></v-autocomplete>
        <v-card-text v-if="selectedProduct">
          <v-skeleton-loader v-if="trendline.loading" type="image">
          </v-skeleton-loader>
          <div v-else>
            <div v-if="trendline.data.length > 0">
              <ApexChart type="area" :options="trendline.options" height="300" :series="trendline.data"></ApexChart>
            </div>
            <div v-else class="text-center text-h6">
              {{ $t('message.no_data') }}
            </div>
          </div>
        </v-card-text>
      </div>
      <v-tabs show-arrows v-model="activeTab" slider-color="tertiary" v-allowed="'view_products'">
        <v-tab :key="'goods'" ripple @click="clickTab(0)">
          {{ $t("message.goods") }}
        </v-tab>
        <v-tab :key="'memberships'" ripple @click="clickTab(1)">
          {{ $t("message.memberships") }}
        </v-tab>
        <v-tab :key="'packages'" ripple @click="clickTab(2)">
          {{ $t("message.packages") }}
        </v-tab>

        <v-tabs-items v-model="activeTab">
          <v-tab-item :key="'goods'" class="pa-2">
            <v-card elevation="0">
              <div class="pb-0">
                <v-text-field outlined dense :label="$t('message.filter_products')" v-model="filterProductsValue"
                  clearable></v-text-field>
              </div>
              <div class="pa-0">
                <v-expansion-panels multiple flat v-if="categorizedGoods" v-model="selectedCategory">
                  <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'"
                    v-for="(category, index1) in filteredCategories" :key="'category_' + index1" :value="false">
                    <v-expansion-panel-header @click="clickCategory(index1)">
                      <div>
                        {{ category == "uncategorized" ? $t("message.uncategorized") : category }}
                        ({{ categorizedGoods[category] ? categorizedGoods[category].length : 0 }})
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-list v-if="categorizedGoods[category].length > 0">
                        <template v-for="(i, index2) in categorizedGoods[category]">
                          <v-list-item :key="'category_item_' + index1 + index2" link @click="doNothing()">
                            <v-list-item-avatar @click="navigateTo({
                              name: 'manageproduct',
                              params: { productId: i._id },
                            })">
                              <v-badge v-if="i.type == 'good' && i.color" bordered bottom size="50" :color="i.color"
                                offset-x="35" offset-y="15">
                                <v-avatar size="50" color="grey lighten-4">
                                  <ImageDisplay v-if="i.image" :src="i.image" />
                                  <img v-else :src="assets('logo-circular-watermarked.png')" />
                                </v-avatar>
                              </v-badge>
                              <v-avatar v-else size="50" color="grey lighten-4">
                                <ImageDisplay v-if="i.image" :src="i.image" />
                                <img v-else :src="assets('logo-circular-watermarked.png')" />
                              </v-avatar>
                            </v-list-item-avatar>
                            <v-list-item-content>
                              <v-list-item-title style="font-size: 14px;">
                                <span class="text-subtitle-1 ml-r">
                                  {{ academy.currencySymbol + i.amountDecimal }}
                                </span>
                                <span class="gray--text">{{ i.name }}</span>
                                <v-tooltip v-if="i.taxBehavior" bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <ReceiptTaxIcon v-bind="attrs" v-on="on" size="20" class="hero-icon"
                                      :color="themeColor('accent')"></ReceiptTaxIcon>
                                  </template>
                                  <span>{{ $t("message.taxes_enabled") }}</span>
                                </v-tooltip>
                                <v-tooltip v-if="i.altStripeAccount" bottom>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon v-bind="attrs" v-on="on" size="20" color="accent">mdi-bank-transfer</v-icon>
                                  </template>
                                  <span>{{ $t("message.alternative_stripe_account") }}</span>
                                </v-tooltip>
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                <span class="mr-1" v-if="i.size">{{ $t("message.size") }}: {{ i.size }}</span>
                                <span class="text-caption" v-if="i.availableQuantity != null && !i.unlimited"
                                  :style="'color:' + getInventoryColor(i.availableQuantity)">
                                  ({{ i.availableQuantity }}
                                  {{ $t("message.available") }})
                                </span>
                              </v-list-item-subtitle>
                            </v-list-item-content>
                            <v-list-item-action>
                              <v-menu offset-y>
                                <template v-slot:activator="{ on }">
                                  <v-btn v-on="on" fab text small slot="activator" left>
                                    <v-icon>mdi-dots-vertical</v-icon>
                                  </v-btn>
                                </template>
                                <v-list>
                                  <v-list-item v-allowed="'edit_products'" @click.stop="
                                    navigateTo({
                                      name: 'manageproduct',
                                      params: { productId: i._id },
                                    })
                                    ">
                                    <v-list-item-title>
                                      <PencilAltIcon size="20" color="gray" class="hero-icon-button"></PencilAltIcon>
                                      {{ $t("message.edit") }}
                                    </v-list-item-title>
                                  </v-list-item>
                                  <v-list-item v-allowed="'sell_items'" v-if="
                                    i.unlimited ||
                                    !i.hasOwnProperty('availableQuantity') ||
                                    i.availableQuantity == null ||
                                    (i.availableQuantity && i.availableQuantity > 0)
                                  " @click.stop="
                                    navigateTo({
                                      name: 'sellproduct',
                                      params: { productId: i._id },
                                    })
                                    ">
                                    <v-list-item-title>
                                      <v-icon size="20" class="mr-1">mdi-currency-usd
                                      </v-icon>
                                      {{ $t("message.sell") }}
                                    </v-list-item-title>
                                  </v-list-item>
                                  <v-list-item v-if="isConnected && i.amountDecimal > 0" v-allowed="'sell_items'"
                                    @click.stop="openQRCode(i._id)">
                                    <v-list-item-title>
                                      <QrcodeIcon size="20" color="gray" class="hero-icon-button"></QrcodeIcon>
                                      {{ $t("message.print_qr_code") }}
                                    </v-list-item-title>
                                  </v-list-item>
                                </v-list>
                              </v-menu>
                            </v-list-item-action>
                          </v-list-item>
                          <v-divider v-if="index2 != categorizedGoods[category].length - 1"
                            :key="'category_item_divier' + index1 + index2"></v-divider>
                        </template>
                      </v-list>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
            </v-card>
          </v-tab-item>
          <v-tab-item :key="'memberships'" class="pa-2">
            <v-expansion-panels flat v-model="selectedMembershipType">
              <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
                <v-expansion-panel-header>
                  <div>
                    <v-icon class="mr-1">mdi-ticket-confirmation-outline</v-icon>
                    <span v-if="paymentsReady">{{ $t("message.manual") }}</span>
                    <span v-else>{{ $t("message.memberships") }} </span>
                    ({{ memberships.length }})
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list two-line dense class="text-left mt-2">
                    <template v-for="(i, index) in memberships">
                      <v-list-item :key="'membership_' + index" @click="doNothing()">
                        <v-list-item-content>
                          <h5>
                            {{ i.name }}
                          </h5>
                          <v-list-item-subtitle>
                            <v-tooltip v-if="i.taxBehavior" bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <ReceiptTaxIcon v-bind="attrs" v-on="on" size="20" class="hero-icon"
                                  :color="themeColor('accent')"></ReceiptTaxIcon>
                              </template>
                              <span>{{ $t("message.taxes_enabled") }}</span>
                            </v-tooltip>
                          </v-list-item-subtitle>
                          <v-list-item-subtitle class="mb-1">
                            {{ i.amountDecimal }} {{ academy.currency }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-menu offset-y>
                            <template v-slot:activator="{ on }">
                              <v-btn v-on="on" fab text small slot="activator" left>
                                <v-icon>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <v-list>
                              <v-list-item @click.stop="
                                navigateTo({
                                  name: 'manageproduct',
                                  params: { productId: i._id },
                                  crumbName: i.name,
                                })
                                ">
                                <v-list-item-title>
                                  <PencilAltIcon color="gray" class="hero-icon-button"></PencilAltIcon>
                                  {{ $t("message.edit") }}
                                </v-list-item-title>
                              </v-list-item>
                              <v-list-item @click.stop="
                                navigateTo({
                                  name: 'sellproduct',
                                  params: { productId: i._id },
                                })
                                ">
                                <v-list-item-title>
                                  <v-icon class="mr-1">mdi-currency-usd</v-icon>
                                  {{ $t("message.sell") }}
                                </v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="index + 1 < subscriptions.length" :key="index"></v-divider>
                    </template>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel v-if="paymentsReady"
                :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
                <v-expansion-panel-header>
                  <div>
                    <v-icon class="mr-1">mdi-refresh</v-icon>
                    {{ $t("message.automatic") }} ({{ filteredSubscriptions.length }})
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-list two-line dense class="text-left mt-2" v-if="paymentsReady">
                    <v-card-text v-if="!isConnected">
                      <h3>{{ $t("message.need_connect_stripe_subscriptions") }}</h3>
                    </v-card-text>
                    <v-list-item v-if="isConnected">
                      <v-list-item-action>
                        <v-icon>mdi-tune-vertical</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>
                          <span v-if="academyBillDay">
                            {{ $t("message.academy_bill_day") }}: <b>{{ academyBillDay }}</b>
                          </span>
                          <span v-else>{{ $t("message.no_academy_bill_day") }}</span>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ $t("message.academy_bill_day_explianed") }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn text small>
                          <v-icon @click="showEditBillDayDialog()">mdi-square-edit-outline</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                    <template v-for="(i, index) in filteredSubscriptions">
                      <v-list-item :key="'subscription_' + index" @click="doNothing()">
                        <v-list-item-content>
                          <h5>
                            {{ i.name }}
                            <v-tooltip v-if="i.taxBehavior" bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on" size="20"
                                  color="accent">mdi-label-percent-outline</v-icon>
                              </template>
                              <span>{{ $t("message.taxes_enabled") }}</span>
                            </v-tooltip>
                            <v-tooltip v-if="i.altStripeAccount" bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon v-bind="attrs" v-on="on" size="20" color="accent">mdi-bank-transfer</v-icon>
                              </template>
                              <span>{{ $t("message.alternative_stripe_account") }}</span>
                            </v-tooltip>
                          </h5>
                          <v-list-item-subtitle class="mb-1 mt-1">
                            <span><b>{{ academy.currencySymbol }}{{ i.amountDecimal }}</b></span>
                            {{ getIntervalDisplay(i.interval, i.customDaysInterval) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-menu offset-y>
                            <template v-slot:activator="{ on }">
                              <v-btn v-on="on" text small slot="activator" left>
                                <v-icon>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <v-list>
                              <v-list-item @click.stop="
                                navigateTo({
                                  name: 'manageproduct',
                                  params: { productId: i._id },
                                  crumbName: i.name,
                                })
                                ">
                                <v-list-item-title>
                                  <v-icon>mdi-mdi-square-edit-outline</v-icon>
                                  {{ $t("message.edit") }}
                                </v-list-item-title>
                              </v-list-item>
                              <v-list-item @click.stop="
                                navigateTo({
                                  name: 'sellproduct',
                                  params: { productId: i._id },
                                })
                                ">
                                <v-list-item-title>
                                  <v-icon>mdi-currency-usd</v-icon>
                                  {{ $t("message.sell") }}
                                </v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </v-list-item-action>
                      </v-list-item>
                      <v-divider v-if="index + 1 < subscriptions.length" :key="index"></v-divider>
                    </template>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-tab-item>
          <v-tab-item :key="'packages'" class="pa-2">
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn outlined color="primary" @click="addPackageDialog = true; newAcademyPackage = {}">{{
                $t("message.add")
              }}</v-btn>
            </v-card-actions>
            <v-skeleton-loader v-if="loadingPackages" type="list-item, list-item">
            </v-skeleton-loader>
            <div v-else-if="packages.length == 0" class="text-center text-subtitle-1">
              {{ $t("message.add_packages_explained") }}
            </div>
            <v-list dense v-else>
              <template v-for="(i, index) in packages">
                <v-list-item :key="'products_package_' + index" @click="goToPackage(i)">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ i.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle>{{ i.description }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-menu offset-y>
                      <template v-slot:activator="{ on }">
                        <v-btn v-on="on" fab text small slot="activator" left>
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-item @click.stop="goToPackage(i)">
                          <v-list-item-title>
                            <PencilAltIcon color="gray" class="hero-icon-button"></PencilAltIcon>
                            {{ $t("message.edit") }}
                          </v-list-item-title>
                        </v-list-item>
                        <v-list-item @click.stop="
                          navigateTo({
                            name: 'sellproduct',
                            params: { productId: 'package' },
                            query: { packageId: i._id }
                          })
                          ">
                          <v-list-item-title>
                            <v-icon class="mr-1">mdi-currency-usd</v-icon>
                            {{ $t("message.sell") }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="index + 1 < subscriptions.length" :key="index"></v-divider>
              </template>
            </v-list>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
      <v-expansion-panels>
        <v-expansion-panel style="margin-top:10px" v-if="inactive && inactive.length > 0">
          <v-expansion-panel-header>
            <div class="text-left">
              {{ $t("message.inactive") }} ({{
                inactive.length
              }})
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-list two-line class="text-left">
              <template v-for="(i, index) in inactive">
                <v-list-item :key="'inactive_' + index">
                  <v-list-item-content>
                    <v-list-item-title style="font-size: 14px;">
                      {{ i.name }}
                    </v-list-item-title>
                    <v-list-item-title>{{ i.description }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn @click="reactivate(i)" icon small>
                      <ReplyIcon class="hero-button" dark></ReplyIcon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="index + 1 < subscriptions.length" :key="index"></v-divider>
              </template>
            </v-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-dialog v-model="academyBillDayDialog" width="90%">
        <v-card outlined>
          <v-card-text>
            <v-text-field outlined :label="$t('message.academy_bill_day')" v-model="academyBillDay"></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-btn outlined elevation="0" color="primary" @click="updateAcademyBillDay()">
              {{ $t("message.update") }}
            </v-btn>
            <v-btn elevation="0" outlined class="outlined-btn"
              @click="academyBillDayDialog = false; academyBillDay = academy.academyBillDay;">
              {{
                $t("message.cancel")
              }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="addPackageDialog" width="90%">
        <v-card outlined>
          <v-card-title>
            {{
              $t("message.add_package")
            }}
          </v-card-title>
          <v-card-text>
            <v-text-field outlined dense :label="$t('message.name')" v-model="newProductsPackage.name"></v-text-field>
            <v-text-field outlined dense :label="$t('message.description')"
              v-model="newProductsPackage.description"></v-text-field>
            <v-autocomplete return-object @change="selectMainPackageProduct" v-model="newProductsPackage.mainProduct"
              :items="availableProductsForMain" item-text="name" :label="$t('message.main_product')" item-value="_id"
              clearable outlined dense></v-autocomplete>
            <v-select outlined multiple :label="$t('message.add_more_products')" dense
              :items="availableProductsForExtra" v-model="newProductsPackage.products"></v-select>
          </v-card-text>
          <v-card-actions>
            <v-btn :loading="loadingPackages" outlined elevation="0" color="primary" @click="addNewProductsPackage()">
              {{ $t("message.add") }}
            </v-btn>
            <v-btn elevation="0" outlined class="outlined-btn"
              @click="addPackageDialog = false; newProductsPackage = {}">
              {{
                $t("message.cancel")
              }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import Api from "@/services/Api2";
import { Environment } from "@/services/Environment";
import ApexChart from 'vue-apexcharts';

import {
  BookmarkAltIcon,
  CloudDownloadIcon,
  PencilAltIcon,
  QrcodeIcon,
  ReceiptTaxIcon,
} from '@vue-hero-icons/outline';

export default {
  data() {
    return {
      urlTab: this.$route.params.tabId,
      urlCategory: this.$route.params.category,
      activeTab: 0,
      selectedCategory: [],
      loading: true,
      isConnected: false,
      allowPublicPurchases: false,
      academy: null,
      selectedProduct: null,
      trendline: { data: [], options: null, products: [], loading: false },
      subscriptionTypes: [],
      filterProductsValue: "",
      categories: [],
      academyBillDay: null,
      academyBillDayDialog: false,
      filteredCategories: [],
      packages: [],
      availableProductsForMain: [],
      availableProductsForExtra: [],
      addPackageDialog: false,
      loadingPackages: true,
      newProductsPackage: {},
      categorizedGoods: {},
      subscriptions: [],
      paymentsReady: true,
      goods: [],
      filteredSubscriptions: [],
      memberships: [],
      selectedMembershipType: 1,
      inactive: [],
      hasProducts: false,
      goodsLength: 0,
      filteredGoodsLength: 0,
      managedAcademy: this.$store.state.managedAcademy,
      intervals: [
        { text: this.$t("message.daily"), value: "day" },
        { text: this.$t("message.weekly"), value: "week" },
        { text: this.$t("message.monthly"), value: "month" },
        { text: this.$t("message.yearly"), value: "year" },
      ],
    };
  },
  components: {
    CloudDownloadIcon,
    PencilAltIcon,
    BookmarkAltIcon,
    QrcodeIcon,
    ReceiptTaxIcon,
    ApexChart,
  },
  mounted() {
    if (this.urlTab) {
      try {
        const n = new Number(this.urlTab);
        this.activeTab = n.valueOf();
        if (this.activeTab == 2)
          this.getPackages();
      } catch (err) {
        console.log('Invalid tab: ', this.urlTab)
      }
    }
  },
  created() {
    this.checkAcademyStatus();
    if (!this.$store.state.managedAcademy) {
      this.navigateTo({ name: "noacademy" });
      return;
    }
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        this.academy = response.data;
        if (!response.data.paymentsReady)
          this.selectedMembershipType = 0;
        this.academyBillDay = response.data.academyBillDay > 0 ? response.data.academyBillDay : 0;
        this.paymentsReady = this.academy.paymentsReady ? this.academy.paymentsReady : false;
        Api()
          .get(
            "admins/academy/" +
            this.$store.state.managedAcademy +
            "/is_connected/all"
          )
          .then((response) => {
            this.isConnected = response.data.stripeConnected;
            if (this.isConnected) {
              if (!this.$store.state.stripeAccountId)
                this.$store.dispatch("setStripeAccountId", response.data.id);
            } else if (response.data.paymentsModule == 'easypay') {
              this.isConnected = true;
            }
            this.loadPage();
          });
      });
  },
  watch: {
    '$route'(to) {
      this.activeTab = to.params.tabId;
    },
    filterProductsValue: {
      immediate: false,
      handler: function (newValue) {
        this.fillCategories(newValue);
      },
    },
  },
  methods: {
    goToPackage(p) {
      this.navigateTo({ name: "manageproductspackage", params: { packageId: p._id }, });
    },
    selectMainPackageProduct(e) {
      this.newProductsPackage.mainProduct = e._id;
      this.newProductsPackage.mainProductObject = e;
      this.fillAvalableExtraProducts();
    },
    addNewProductsPackage() {
      if (!this.newProductsPackage
        || !this.newProductsPackage.name
        || !this.newProductsPackage.description
        || !this.newProductsPackage.mainProduct
        || !this.newProductsPackage.products) {
        this.showAlert("error", this.$t("message.fill_all_parameters"));
        return;
      }
      this.loadingPackages = true;
      Api()
        .post("admins/managedacademy/" + this.$store.state.managedAcademy + "/products/packages", this.newProductsPackage)
        .then(() => {
          this.showAlert("success", this.$t("message.success"));
          this.getPackages();
          this.addPackageDialog = false;
          this.newProductsPackage = {};
        })
        .catch((e) => {
          if (e) {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    fillAvalableExtraProducts() {
      if (this.newProductsPackage && this.newProductsPackage.mainProductObject) {
        for (const i of this.goods) {
          //Add products to our lists for packages
          let taxMatches = true;
          if (this.newProductsPackage.mainProductObject.taxBehavior && i.taxBehavior != this.mainProductObject.taxBehavior)
            taxMatches = false;
          if (taxMatches) {
            this.availableProductsForExtra.push({ text: i.name, value: i._id, product: i })
          }
        }
      }
    },
    getPackages() {
      this.loadingPackages = true;
      Api()
        .get("admins/managedacademy/" + this.$store.state.managedAcademy + "/products/packages")
        .then((response) => {
          this.packages = response.data.data;
          this.loadingPackages = false;
        })
        .catch((e) => {
          if (e) {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    getProductTrendline() {
      if (this.selectedProduct) {
        this.trendline.loading = true;
        this.trendline.data = [];
        Api()
          .get(
            "payments/academy/" + this.$store.state.managedAcademy + "/product/" + this.selectedProduct + "/transactions/trendline"
          )
          .then((response) => {
            const data = response.data.data;
            let categories = [];
            let series = [];
            if (data.length > 0) {
              const productName = response.data.product;
              let keys = {};
              for (const i of data) {
                const monthString = this.$d(new Date(i.date), "month_day", this.$i18n.locale);
                keys[i.key] = {
                  date: monthString,
                  count: i.count
                };
              }
              let today = new Date();
              let firstDay = new Date(data[0].date);
              //loop over all days
              for (let d1 = new Date(firstDay); d1 <= today; d1.setDate(d1.getDate() + 1)) {
                const key =
                  d1.getFullYear() +
                  "-" +
                  (new Number(d1.getMonth()).valueOf() + 1) +
                  "-" +
                  d1.getDate();
                const monthString = this.$d(new Date(d1), "month_day_year", this.$i18n.locale);
                categories.push(monthString)
                const v = keys[key];
                if (v) {
                  series.push(v.count);
                } else {
                  series.push(0);
                }
              }

              this.trendline.data = [{
                name: productName,
                data: series
              }];
              this.trendline.options = {
                colors: [this.themeColor('primary')],
                xaxis: {
                  categories: categories,
                  type: 'datetime',
                  labels: {
                    show: true,
                  }
                },
                yaxis: {
                  labels: {
                    show: true,
                    formatter: function (val) {
                      return val.toFixed(0);
                    }
                  }
                },
                dataLabels: {
                  enabled: false,
                },
                stroke: {
                  curve: 'smooth'
                },
                theme: {
                  mode: this.isDarkMode() ? 'dark' : 'light',
                },
                chart: {
                  toolbar: {
                    show: false
                  },
                  background: 'none'
                },
                legend: {
                  show: false
                },
                grid: {
                  show: false
                },
              };
            }
            this.trendline.loading = false;
          })
          .catch((err) => {
            console.log(err)
            this.trendline.loading = false;
          });
      } else {
        this.trendline.loading = false;
        this.trendline.data = [];
      }
    },
    clickCategory(index) {
      this.navigateTo({
        name: "productscategory",
        params: { tabId: 0, category: index },
      });
    },
    openQRCode(id) {
      let purchaseProductUrl = Environment.get("VUE_APP_API_HOST") + '/pub/academy/' + this.$store.state.selectedAcademy + '/product/' + id;
      let buff = new Buffer(purchaseProductUrl);
      const encodedUrl = buff.toString("base64");
      window.open(Environment.get("VUE_APP_ASSETS_HOME") + 'secure/qr/' + encodedUrl + '?sp=product&p=' + id);
    },
    updateAcademyBillDay() {
      const data = [
        {
          propName: 'academyBillDay',
          value: this.academyBillDay,
        },
      ];
      Api()
        .patch("admins/academy/" + this.$store.state.selectedAcademy, { values: data, })
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert("success", this.$t("message.updated"));
            this.academyBillDayDialog = false;
          }
        })
        .catch((e) => {
          if (e) {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    showEditBillDayDialog() {
      this.academyBillDayDialog = true;
    },
    clickTab(tab) {
      if (tab != this.activeTab) {
        this.navigateTo({
          name: "productstabs",
          params: { tabId: tab },
        });
      }
      if (tab == 2)
        this.getPackages();
    },
    getInventoryColor(amount) {
      if (amount == 0) return "red";
      else if (amount > 0 && amount <= 5) return "orange";
      else return "green";
    },
    fillCategories(value) {
      if (value) value = value.toLowerCase();
      try {
        var count = 0;
        if (this.categories && this.categories.length > 0) {
          this.categorizedGoods["uncategorized"] = [];
          for (const c of this.categories) this.categorizedGoods[c] = [];
          //Now proceed to add all the products
          var tmpCategories = [];
          if (this.goods) {
            this.goodsLength = this.goods.length;
            for (const g of this.goods) {
              if (g.allowPublicPurchase)
                this.allowPublicPurchases = true;
              if (
                !value ||
                value == "" ||
                g.name.toLowerCase().indexOf(value) >= 0
              ) {
                if (g.category) {
                  this.categorizedGoods[g.category].push(g);
                  if (tmpCategories.indexOf(g.category) < 0)
                    tmpCategories.push(g.category);
                } else this.categorizedGoods["uncategorized"].push(g);
                count++;
              }
            }
          }
          this.filteredCategories = tmpCategories.sort();
          if (this.categorizedGoods["uncategorized"].length > 0)
            this.filteredCategories.push("uncategorized");
          this.filteredGoodsLength = count;
          if (this.subscriptions) {
            var filtered = [];
            for (const i of this.subscriptions) {
              if (i.allowPublicPurchase)
                this.allowPublicPurchases = true;
              if (
                !value ||
                value == "" ||
                i.name.toLowerCase().indexOf(value) >= 0
              ) {
                filtered.push(i);
              }
            }
            this.filteredSubscriptions = filtered;
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    getProductsCSV() {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/products/export/csv"
        )
        .then((response) => {
          let blob = new Blob([response.data], { type: "text/csv" });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download =
            this.academy.name +
            "_Products_" +
            new Date().toISOString() +
            "_" +
            ".csv";
          link.click();
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    loadPage() {
      this.getProducts();
    },
    reactivate(p) {
      console.log(p);
    },
    getProducts() {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/products/get/categories"
        )
        .then((response) => {
          this.categories = response.data;
          this.categories.push("uncategorized");
          Api()
            .get(
              "admins/academy/" + this.$store.state.managedAcademy + "/products"
            )
            .then((response) => {
              const data = response.data;
              if (data) {
                let memberships = [];
                let goods = [];
                this.subscriptions = data.subscriptions;
                this.availableProductsForMain.push({ header: this.$t("message.automatic_memberships") });
                for (const i of this.subscriptions) {
                  this.availableProductsForMain.push({ taxBehavior: i.taxBehavior, name: i.name, amountDecimal: i.amountDecimal, _id: i._id })
                }
                this.inactive = data.inactive;
                for (const g of data.goods) {
                  if (g.isMembership) {
                    memberships.push(g);
                  } else {
                    goods.push(g);
                  }
                  this.trendline.products.push({
                    text: g.name,
                    value: g._id
                  });
                }
                this.goods = goods;
                this.memberships = memberships;
                this.availableProductsForMain.push({ header: this.$t("message.manual_memberships") });
                for (const i of this.memberships)
                  this.availableProductsForMain.push({ taxBehavior: i.taxBehavior, name: i.name, amountDecimal: i.amountDecimal, _id: i._id })
                this.availableProductsForMain.push({ header: this.$t("message.goods") });
                for (const i of this.goods)
                  this.availableProductsForMain.push({ taxBehavior: i.taxBehavior, name: i.name, amountDecimal: i.amountDecimal, _id: i._id })
                if (this.goods && this.goods.length > 0)
                  this.hasProducts = true;
                this.fillCategories();
                this.loading = false;
                if (this.urlCategory && !isNaN(this.urlCategory)) {
                  this.selectedCategory = [new Number(this.urlCategory).valueOf()];
                }
              }
            })
            .catch((e) => {
              this.loading = false;
              if (e)
                this.showAlert(
                  "error",
                  this.$t("message.error_generic")
                );
            });
        });
    },
  },
};
</script>

<style scoped></style>
